/**
 * WSIEStickyNote — Physically realistic sticky note component.
 * Paper texture · drop shadow · corner curl · tape/pin/magnet attachments
 * Draggable · Resizable · Collapsible · Full lifecycle controls
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  X, Check, Eye, EyeOff, Clock, AlertTriangle, User, Calendar,
  ChevronDown, ChevronUp, Edit3, Archive, RotateCcw, Paperclip,
  History, MoreHorizontal, Minus,
} from 'lucide-react';
import {
  useWSIEStore, NOTE_COLORS, NOTE_TYPE_META, getDueStatus,
  type StickyNote, type NoteColor,
} from '../../stores/wsie.store.js';
import { useAuthStore } from '../../stores/auth.store.js';

// ── Paper texture CSS (inline data URI grain) ─────────────────────────────────
const PAPER_GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23noise)' opacity='0.045'/%3E%3C/svg%3E")`;

function colorStyle(color: NoteColor, opacity: number) {
  const c = NOTE_COLORS[color];
  return {
    bg: c.bg,
    text: c.text,
    border: c.border,
    accent: c.accent,
    shadow: `0 4px 8px ${c.shadow}, 0 8px 24px ${c.shadow}, 0 2px 4px rgba(0,0,0,0.15)`,
  };
}

// ── Attachment visual at top (tape / pin / magnet) ────────────────────────────
function NoteAttachment({ style, color }: { style: StickyNote['style']; color: NoteColor }) {
  const c = NOTE_COLORS[color];
  if (style === 'tape') return (
    <div style={{
      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
      width: 52, height: 18,
      background: 'rgba(173,210,230,0.65)',
      backdropFilter: 'blur(2px)',
      borderRadius: 3,
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      border: '1px solid rgba(173,210,230,0.9)',
    }} />
  );
  if (style === 'pin') return (
    <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)' }}>
      <div style={{ width: 14, height: 14, borderRadius: '50%', background: c.accent, boxShadow: `0 2px 6px ${c.shadow}, inset 0 1px 2px rgba(255,255,255,0.4)`, border: '1px solid rgba(0,0,0,0.1)' }} />
      <div style={{ width: 2, height: 10, background: '#94a3b8', margin: '0 auto', borderRadius: '0 0 2px 2px' }} />
    </div>
  );
  if (style === 'magnet') return (
    <div style={{ position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
      <div style={{ width: 10, height: 14, borderRadius: '5px 5px 0 0', background: '#ef4444', border: '1px solid rgba(0,0,0,0.15)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      <div style={{ width: 10, height: 14, borderRadius: '5px 5px 0 0', background: '#3b82f6', border: '1px solid rgba(0,0,0,0.15)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
  return null;
}

// ── Corner curl ───────────────────────────────────────────────────────────────
function CornerCurl({ color }: { color: NoteColor }) {
  const c = NOTE_COLORS[color];
  return (
    <div style={{
      position: 'absolute', bottom: 0, right: 0, width: 20, height: 20,
      background: `linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.13) 50%)`,
      borderRadius: '0 0 6px 0',
      pointerEvents: 'none',
    }} />
  );
}

// ── Due date color ─────────────────────────────────────────────────────────────
function dueBadgeStyle(status: 'ok' | 'soon' | 'overdue' | null) {
  if (!status) return {};
  const map = { ok: { bg: '#dcfce7', color: '#16a34a' }, soon: { bg: '#fef9c3', color: '#ca8a04' }, overdue: { bg: '#fee2e2', color: '#dc2626' } };
  return map[status];
}

// ── Completion dialog ─────────────────────────────────────────────────────────
function CompletionDialog({ noteId, onClose }: { noteId: string; onClose: () => void }) {
  const [comment, setComment] = useState('');
  const user = useAuthStore((s) => s.user?.email ?? 'user');
  const { completeNote } = useWSIEStore();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }}>
      <div style={{ background: '#0f172a', border: '1px solid rgba(94,234,212,0.3)', borderRadius: 14, padding: 24, width: 340 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#e2ecfa', marginBottom: 8 }}>Mark as Completed?</div>
        <textarea placeholder="Optional completion note..."
          value={comment} onChange={(e) => setComment(e.target.value)}
          style={{ width: '100%', minHeight: 70, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(71,85,105,0.5)', borderRadius: 8, padding: 10, color: '#e2ecfa', fontSize: 12, resize: 'none', marginBottom: 14 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button onClick={() => { completeNote(noteId, user, comment); onClose(); }}
            style={{ padding: '8px', borderRadius: 8, border: '1px solid rgba(74,222,128,0.4)', background: 'rgba(74,222,128,0.1)', color: '#4ade80', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            ✓ Complete
          </button>
          <button onClick={onClose}
            style={{ padding: '8px', borderRadius: 8, border: '1px solid rgba(71,85,105,0.4)', background: 'transparent', color: 'rgba(148,163,184,0.8)', fontSize: 12, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Close confirmation dialog ────────────────────────────────────────────────
function CloseDialog({ noteId, onClose, userEmail }: { noteId: string; onClose: () => void; userEmail: string }) {
  const { completeNote, hideForUser, archiveNote } = useWSIEStore();
  const [snoozeTime, setSnoozeTime] = useState('');
  const { snoozeNote } = useWSIEStore();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }}>
      <div style={{ background: '#0f172a', border: '1px solid rgba(71,85,105,0.4)', borderRadius: 14, padding: 24, width: 300 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#e2ecfa', marginBottom: 16 }}>What would you like to do?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { icon: '✅', label: 'Mark Complete', action: () => { completeNote(noteId, userEmail); onClose(); } },
            { icon: '👁', label: 'Hide for Me Only', action: () => { hideForUser(noteId, userEmail); onClose(); } },
            { icon: '📦', label: 'Archive Note', action: () => { archiveNote(noteId, userEmail); onClose(); } },
          ].map((opt) => (
            <button key={opt.label} onClick={opt.action}
              style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid rgba(71,85,105,0.4)', background: 'rgba(255,255,255,0.03)', color: '#e2ecfa', fontSize: 12, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 14 }}>{opt.icon}</span>{opt.label}
            </button>
          ))}
          <button onClick={onClose}
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid rgba(71,85,105,0.3)', background: 'transparent', color: 'rgba(148,163,184,0.6)', fontSize: 12, cursor: 'pointer' }}>
            ❌ Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main sticky note ──────────────────────────────────────────────────────────
export function WSIEStickyNote({ note }: { note: StickyNote }) {
  const userEmail = useAuthStore((s) => s.user?.email ?? 'user');
  const { updateNote, moveNote, completeNote, acknowledgeNote } = useWSIEStore();

  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [editTitle, setEditTitle] = useState(note.title);
  const [showComplete, setShowComplete] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ mx: 0, my: 0, nx: 0, ny: 0 });
  const noteRef = useRef<HTMLDivElement>(null);

  const cs = colorStyle(note.color, note.opacity);
  const meta = NOTE_TYPE_META[note.type];
  const dueStatus = getDueStatus(note);
  const isAcknowledged = note.acknowledgedBy.includes(userEmail);

  // Drag to reposition
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, textarea, input, select')) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { mx: e.clientX, my: e.clientY, nx: note.posX, ny: note.posY };
  }, [note.posX, note.posY]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = ((e.clientX - dragStart.current.mx) / window.innerWidth) * 100;
      const dy = ((e.clientY - dragStart.current.my) / window.innerHeight) * 100;
      moveNote(note.id, Math.max(0, Math.min(85, dragStart.current.nx + dx)), Math.max(0, Math.min(85, dragStart.current.ny + dy)));
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isDragging, note.id, moveNote]);

  const saveEdit = () => {
    updateNote(note.id, { title: editTitle, content: editContent });
    setEditing(false);
  };

  return (
    <>
      <div
        ref={noteRef}
        data-testid={`wsie-note-${note.id}`}
        style={{
          position: 'fixed',
          left: `${note.posX}%`,
          top:  `${note.posY}%`,
          width: note.width,
          minHeight: collapsed ? 'auto' : note.height,
          zIndex: 9500 + (isDragging ? 100 : 0),
          transform: `rotate(${note.rotation}deg)`,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          filter: note.status === 'completed' ? 'saturate(0.4) opacity(0.6)' : undefined,
          opacity: note.opacity,
          transition: isDragging ? 'none' : 'box-shadow .2s, opacity .3s',
        }}
        onMouseDown={onMouseDown}
      >
        {/* ── Attachment (tape / pin / magnet) ─────────────────────────── */}
        <NoteAttachment style={note.style} color={note.color} />

        {/* ── Note body ────────────────────────────────────────────────── */}
        <div style={{
          background: cs.bg,
          backgroundImage: PAPER_GRAIN,
          border: `1px solid ${cs.border}`,
          borderRadius: 4,
          boxShadow: isDragging
            ? `0 12px 40px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2)`
            : cs.shadow,
          overflow: 'visible',
          position: 'relative',
          // Line ruling for yellow notes
          backgroundBlendMode: 'multiply',
        }}>

          {/* ── Header ─────────────────────────────────────────────────── */}
          <div style={{
            padding: '8px 10px 6px',
            borderBottom: `1px solid ${cs.border}88`,
            display: 'flex', alignItems: 'flex-start', gap: 6,
            background: `linear-gradient(180deg, ${cs.border}44 0%, transparent 100%)`,
          }}>
            {/* Type icon */}
            <span style={{ fontSize: 13, flexShrink: 0, lineHeight: 1.4 }}>{meta.icon}</span>

            {/* Title */}
            {editing ? (
              <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 11, fontWeight: 700, color: cs.text, fontFamily: 'inherit' }}
              />
            ) : (
              <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: cs.text, lineHeight: 1.4, wordBreak: 'break-word' }}>
                {note.title}
              </span>
            )}

            {/* Due status badge */}
            {dueStatus && (
              <div style={{ padding: '1px 5px', borderRadius: 3, fontSize: 8, fontWeight: 700, ...dueBadgeStyle(dueStatus) }}>
                {dueStatus === 'overdue' ? 'OVERDUE' : dueStatus === 'soon' ? 'DUE SOON' : ''}
              </div>
            )}

            {/* Collapse */}
            <button onClick={(e) => { e.stopPropagation(); setCollapsed((v) => !v); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: cs.accent, padding: 1, flexShrink: 0 }}>
              {collapsed ? <ChevronDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            </button>

            {/* Close */}
            <button onClick={(e) => { e.stopPropagation(); setShowCloseDialog(true); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: cs.accent, padding: 1, flexShrink: 0 }}>
              <X className="h-3 w-3" />
            </button>
          </div>

          {!collapsed && (
            <div style={{ padding: '8px 10px 10px' }}>
              {/* Content */}
              {editing ? (
                <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)}
                  style={{ width: '100%', minHeight: 70, background: 'transparent', border: 'none', outline: 'none', resize: 'vertical', fontSize: 12, color: cs.text, fontFamily: 'inherit', lineHeight: 1.5 }}
                />
              ) : (
                <div style={{ fontSize: 12, color: cs.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: 50 }}>
                  {note.content || <span style={{ opacity: 0.4, fontStyle: 'italic' }}>Empty note...</span>}
                </div>
              )}

              {/* Meta footer */}
              <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${cs.border}66` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {/* Creator */}
                  <span style={{ fontSize: 9, color: `${cs.text}88`, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <User style={{ width: 8, height: 8 }} />
                    {note.creator.split('@')[0]}
                  </span>
                  {/* Date */}
                  <span style={{ fontSize: 9, color: `${cs.text}66` }}>
                    {new Date(note.createdAt).toLocaleDateString()}
                  </span>
                  {/* Assigned */}
                  {note.assignedTo && (
                    <span style={{ fontSize: 9, color: cs.accent, background: `${cs.accent}18`, padding: '0 4px', borderRadius: 3 }}>
                      → {note.assignedTo.split('@')[0]}
                    </span>
                  )}
                  {/* Priority */}
                  <span style={{
                    fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
                    background: note.priority === 'critical' ? '#fee2e2' : note.priority === 'high' ? '#ffedd5' : note.priority === 'medium' ? '#fef9c3' : '#f0fdf4',
                    color:      note.priority === 'critical' ? '#dc2626' : note.priority === 'high' ? '#ea580c' : note.priority === 'medium' ? '#ca8a04' : '#16a34a',
                  }}>
                    {note.priority.toUpperCase()}
                  </span>
                </div>

                {/* Anchor */}
                {note.anchorLabel && note.anchorLabel !== 'General Workspace' && (
                  <div style={{ fontSize: 9, color: `${cs.text}66`, marginTop: 3 }}>
                    📍 {note.anchorLabel}
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 4, marginTop: 7 }}>
                  {/* Acknowledge */}
                  {!isAcknowledged && (
                    <button onClick={(e) => { e.stopPropagation(); acknowledgeNote(note.id, userEmail); }}
                      style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, border: `1px solid ${cs.border}`, background: 'transparent', color: cs.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Eye style={{ width: 8, height: 8 }} /> ACK
                    </button>
                  )}
                  {isAcknowledged && (
                    <span style={{ fontSize: 9, color: `${cs.accent}88`, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Check style={{ width: 8, height: 8 }} /> Acknowledged
                    </span>
                  )}

                  {/* Edit */}
                  {note.creator === userEmail && !editing && (
                    <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                      style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, border: `1px solid ${cs.border}`, background: 'transparent', color: cs.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Edit3 style={{ width: 8, height: 8 }} /> Edit
                    </button>
                  )}
                  {editing && (
                    <button onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                      style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, border: `1px solid ${cs.border}`, background: `${cs.accent}18`, color: cs.accent, cursor: 'pointer' }}>
                      Save
                    </button>
                  )}

                  {/* Complete */}
                  {note.status === 'active' && (
                    <button onClick={(e) => { e.stopPropagation(); setShowComplete(true); }}
                      style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, border: '1px solid rgba(74,222,128,0.4)', background: 'rgba(74,222,128,0.1)', color: '#16a34a', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, marginLeft: 'auto' }}>
                      <Check style={{ width: 8, height: 8 }} /> Done
                    </button>
                  )}

                  {/* Timeline toggle */}
                  <button onClick={(e) => { e.stopPropagation(); setShowTimeline((v) => !v); }}
                    style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, border: `1px solid ${cs.border}66`, background: 'transparent', color: `${cs.text}77`, cursor: 'pointer' }}>
                    <History style={{ width: 8, height: 8 }} />
                  </button>
                </div>

                {/* Timeline */}
                {showTimeline && note.timeline.length > 0 && (
                  <div style={{ marginTop: 8, borderTop: `1px solid ${cs.border}55`, paddingTop: 6 }}>
                    {note.timeline.slice(-5).reverse().map((ev) => (
                      <div key={ev.id} style={{ fontSize: 9, color: `${cs.text}77`, marginBottom: 3, display: 'flex', gap: 5 }}>
                        <span style={{ color: cs.accent, fontWeight: 700, flexShrink: 0 }}>{ev.kind}</span>
                        <span>by {ev.actor.split('@')[0]}</span>
                        <span style={{ marginLeft: 'auto', opacity: 0.5 }}>{new Date(ev.at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Corner curl */}
          <CornerCurl color={note.color} />
        </div>
      </div>

      {showComplete && <CompletionDialog noteId={note.id} onClose={() => setShowComplete(false)} />}
      {showCloseDialog && <CloseDialog noteId={note.id} onClose={() => setShowCloseDialog(false)} userEmail={userEmail} />}
    </>
  );
}
